import { useCallback, useEffect } from "react";
import { FormFieldProps } from "../form-field/FormFieldProps";
import { useDistinct, useMultiInputState } from "../FormComponentHooks";
import { IndexedInput } from "./IndexedInput";
import styles from "./MultiInput.module.less";
import { DelegatedFormFieldProps } from "./SingleInput";

export interface MultiInputProps<V, T extends FormFieldProps<V>> extends DelegatedFormFieldProps<V, T> {
  defaultValue?: V;

  // undefined can be a value for newly added fields with empty default
  onValues?(values: (V | undefined)[]): void;

  values?: V[];
}

export const MultiInput = <V, T extends FormFieldProps<V>>(props: MultiInputProps<V, T>) => {
  const { defaultValue, values: valueOverrides, formFieldComponent, additionalFormFieldProps } = props;

  // validate correct input, mode with empty values not supported
  const distinctValueOverrides = useDistinct(valueOverrides);
  useEffect(
    () => {
      if (distinctValueOverrides && distinctValueOverrides.length < 1) {
        console.error("Calling a managed multi-input without a single value.");
      }
    },
    [distinctValueOverrides],
  );


  const [values, setValues] = useMultiInputState<V, T>(props);

  const addValue = useCallback(
    () => setValues((prev) => [...prev, defaultValue]),
    [defaultValue, setValues],
  );

  const removeIndexValue = useCallback(
    (index: number) => {
      setValues((prev) => {
        const nextValues = [...prev];
        nextValues.splice(index, 1);
        return nextValues;
      });
    },
    [setValues],
  );

  const onIndexValue = useCallback(
    (index: number, value: V) => {
      setValues((prev) => {
        const nextValues = [...prev];
        nextValues[index] = value;
        return nextValues;
      });
    },
    [setValues],
  );

  return (
    <div className={styles.multiInputContainer}>
      {values.map((value, i) => (
        <IndexedInput
          key={i}
          index={i}
          defaultValue={defaultValue}
          value={value}
          addValue={addValue}
          removeIndexValue={removeIndexValue}
          onIndexValue={onIndexValue}
          formFieldComponent={formFieldComponent}
          additionalFormFieldProps={additionalFormFieldProps}
        />
      ))}
    </div>
  );
};
